import React, { useEffect, useState } from 'react';

import { createPortal } from 'react-dom';

import { BackdropOptions } from '../../config/hintsConfig';

const DEFAULTS = {
    pad: 3,
    outerPad: 10,
    r: 8,
    offsetX: 0,
    offsetY: 0,
    extraWidth: 0,
    extraHeight: 0,
};

interface SpotDims {
    x: number;
    y: number;
    w: number;
    h: number;
    vw: number;
    vh: number;
}

const getDims = (el: Element, opts: Required<BackdropOptions>): SpotDims => {
    const r = el.getBoundingClientRect();
    const ew = opts.extraWidth;
    const eh = opts.extraHeight;
    return {
        x: r.left - opts.pad - 2 + opts.offsetX - Math.floor(ew / 2),
        y: r.top - opts.pad + 2 + opts.offsetY - Math.floor(eh / 2),
        w: r.width + opts.pad * 2 + ew,
        h: r.height + opts.pad * 2 + eh,
        vw: window.innerWidth,
        vh: window.innerHeight,
    };
};

const buildPath = ({ x, y, w, h, vw, vh }: SpotDims, outerPad: number, r: number): string => {
    const outerRect = `M 0 0 H ${vw} V ${vh} H 0 Z`;
    const ox = x - outerPad;
    const oy = y - outerPad;
    const ow = w + outerPad * 2;
    const oh = h + outerPad * 2;
    const hole =
        `M ${ox + r} ${oy} ` +
        `H ${ox + ow - r} A ${r} ${r} 0 0 1 ${ox + ow} ${oy + r} ` +
        `V ${oy + oh - r} A ${r} ${r} 0 0 1 ${ox + ow - r} ${oy + oh} ` +
        `H ${ox + r} A ${r} ${r} 0 0 1 ${ox} ${oy + oh - r} ` +
        `V ${oy + r} A ${r} ${r} 0 0 1 ${ox + r} ${oy} Z`;
    return `${outerRect} ${hole}`;
};

interface HintBackdropProps {
    targetEl: Element;
    backdropOptions?: BackdropOptions;
}

export const HintBackdrop = ({ targetEl, backdropOptions }: HintBackdropProps) => {
    const opts: Required<BackdropOptions> = { ...DEFAULTS, ...backdropOptions };
    const [dims, setDims] = useState<SpotDims>(() => getDims(targetEl, opts));

    useEffect(() => {
        const recalc = () => setDims(getDims(targetEl, opts));
        window.addEventListener('resize', recalc);
        window.addEventListener('scroll', recalc, true);

        const ro = new ResizeObserver(recalc);
        ro.observe(targetEl);
        ro.observe(document.documentElement);

        return () => {
            window.removeEventListener('resize', recalc);
            window.removeEventListener('scroll', recalc, true);
            ro.disconnect();
        };
    }, [
        targetEl,
        opts.pad,
        opts.outerPad,
        opts.r,
        opts.offsetX,
        opts.offsetY,
        opts.extraWidth,
        opts.extraHeight,
    ]);

    return createPortal(
        <svg
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 'var(--z-hint-backdrop)' as unknown as number,
                pointerEvents: 'none',
            }}
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d={buildPath(dims, opts.outerPad, opts.r)}
                fill="rgba(37, 39, 51, 0.65)"
                fillRule="evenodd"
                style={{ pointerEvents: 'fill' }}
            />
            <rect
                x={dims.x}
                y={dims.y}
                width={dims.w}
                height={dims.h}
                rx={opts.r}
                fill="none"
                stroke="var(--color-accent-base)"
                strokeWidth={2}
                strokeDasharray="8 5"
                style={{ pointerEvents: 'none' }}
            />
        </svg>,
        document.body
    );
};
